import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Loader2,
  AlertCircle,
  ChevronRight,
} from 'lucide-react';
import { getAllLedgerEntries, LedgerEntry } from '../../services/MasterAdmin/accountantService';
import { CommissionBreakdownCell, PlatformOfferCell } from '../../components/Accountant/PlatformOfferCells';

const CommissionLedgerPage: React.FC = () => {
  const navigate = useNavigate();
  const [ledgerEntries, setLedgerEntries] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);

  const fetchLedgerEntries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getAllLedgerEntries({
        search: searchTerm.trim() || undefined,
        page: currentPage,
        limit: 50,
      });

      // Filter out delivery partners
      const filtered = response.data.ledgerEntries.filter(
        (entry) => entry.vendorType !== 'DELIVERY_PARTNER'
      );

      setLedgerEntries(filtered);
      setTotalPages(response.pagination.totalPages);
      setTotalEntries(filtered.length);
    } catch (err: any) {
      console.error('Error fetching ledger entries:', err);
      setError(err.response?.data?.message || 'Failed to fetch ledger entries. Please try again.');
      setLedgerEntries([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    fetchLedgerEntries();
  }, [fetchLedgerEntries]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  const resolveServiceStatus = (entry: LedgerEntry) => {
    const candidate =
      entry.lifecycleStatus ||
      entry.metadata?.appointmentStatus ||
      entry.metadata?.bookingStatus ||
      entry.metadata?.orderStatus ||
      entry.metadata?.serviceStatus ||
      null;
    return candidate ? String(candidate).toUpperCase() : 'N/A';
  };

  const getServiceStatusBadge = (status: string) => {
    const normalized = String(status || 'N/A').toUpperCase();
    let bgColor = 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    if (normalized === 'COMPLETED' || normalized === 'DELIVERED' || normalized === 'SETTLED') {
      bgColor = 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    } else if (normalized === 'PENDING_SERVICE' || normalized === 'PENDING' || normalized === 'CONFIRMED') {
      bgColor = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    } else if (normalized === 'CANCELLED' || normalized === 'REJECTED') {
      bgColor = 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    }
    return (
      <span className={`px-2 py-1 rounded-md text-xs font-medium ${bgColor}`}>
        {normalized}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
        <div>
          <h3 className="font-semibold text-red-800 dark:text-red-400">Error</h3>
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Commission Ledger</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {totalEntries} entries found
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by order ID..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {ledgerEntries.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            No entries found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Order ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Vendor Name
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Platform Offer
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Commission Earned
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    GST Payable
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Total Commission with GST
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Service Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {ledgerEntries.map((entry) => {
                  const totalCommissionWithGst = (entry.commissionAmount || 0) + (entry.gstOnCommission || 0);
                  return (
                    <tr
                      key={entry.ledgerId}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {entry.serviceReferenceId || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {entry.vendorName || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <PlatformOfferCell entry={entry} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <CommissionBreakdownCell entry={entry} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(entry.gstOnCommission || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-bold text-primary-600 dark:text-primary-400">
                          {formatCurrency(totalCommissionWithGst)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getServiceStatusBadge(resolveServiceStatus(entry))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(entry.serviceCompletedAt || undefined)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => navigate(`/accountant/ledger/order/${entry.serviceReferenceId}?ledgerType=commission`)}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300"
                      >
                        View Details
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-4 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommissionLedgerPage;
